import { useEffect, useState } from "react";
import { Box, CssBaseline, Tooltip, Typography, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as themeStyles } from "@mui/material/styles";
import { DataGrid, esES } from "@mui/x-data-grid";
import Sidebar from "../../../../components/sidebar/Sidebar";
import Topbar from "../../../../components/topbar/TopBar";
import Header from "../../../../components/header/Header";
import { tokens } from "../../../../theme";
import { getTokenRefresh } from "../../../../helpers/token/refreshToken";
import { getDrivers } from "../../../../api/users/driver/driver";
import { dateFormat } from "../../../../helpers/date/date";

const renderLongCell = ({ value }) => (
  <Tooltip title={value || ""}>
    <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {value || ""}
    </Box>
  </Tooltip>
);

const columns = [
  { headerName: "ID", field: "index", width: 50 },
  {
    headerName: "NOMBRE",
    field: "fullname",
    type: "string",
    headerAlign: "left",
    align: "left",
    width: 200,
  },
  { headerName: "EMAIL", field: "email", width: 180 },
  { headerName: "TELEFONO", field: "phone", width: 100 },
  {
    headerName: "DIRECCION",
    field: "address",
    width: 150,
    renderCell: renderLongCell,
  },
  { headerName: "DNI", field: "dni", width: 80 },
  {
    headerName: "PLACA",
    field: "plates",
    width: 80,
    renderCell: renderLongCell,
  },
  {
    headerName: "CARROCERIA",
    field: "bodyworks",
    width: 80,
    renderCell: renderLongCell,
  },
  { headerName: "EDAD", field: "date_of_birth", width: 50 },
  {
    headerName: "F. REGISTRO",
    field: "created_at",
    width: 90,
    renderCell: ({ value }) => dateFormat(new Date(value)),
  },
];

export function DriverArchivedScreen() {
  const themeSidebar = themeStyles();
  const matchesSM = useMediaQuery(themeSidebar.breakpoints.down("sm"));
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadArchivedDrivers = async () => {
      try {
        const token = getTokenRefresh();
        if (!token) {
          setError("No se pudo obtener la sesión.");
          return;
        }

        const response = await getDrivers(token);
        const archivedDrivers = Object.values(response.data || {})
          .filter((driver) => driver.status === 4)
          .map((driver, index) => ({
            ...driver,
            index: index + 1,
            plates: Object.values(driver.vehicle || {})
              .map((vehicle) => vehicle.vehicle_plate)
              .filter(Boolean)
              .join(", "),
            bodyworks: Object.values(driver.vehicle || {})
              .map((vehicle) => vehicle.vehicle_bodywork)
              .filter(Boolean)
              .join(", "),
          }));
        setDrivers(archivedDrivers);
      } catch (loadError) {
        setError("No se pudieron cargar los conductores archivados.");
      } finally {
        setLoading(false);
      }
    };

    loadArchivedDrivers();
  }, []);

  return (
    <>
      <CssBaseline />
      <div className="app">
        {!matchesSM && <Sidebar />}
        <main className="content">
          <Topbar />
          <Box m="10px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Header title="CONDUCTORES ARCHIVADOS" subtitle="" />
            </Box>
            {error && <Typography color="error">{error}</Typography>}
            <Box display="grid" gap="10px">
              <Box gridColumn="span 12" gridRow="span 2">
                <Box
                  m="10px 0 0 0"
                  height="100vh"
                  sx={{
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .name-column--cell": { color: colors.greenAccent[300] },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: colors.blueAccent[700],
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                      color: `${colors.greenAccent[200]} !important`,
                    },
                  }}
                >
                  <DataGrid
                    getRowId={(row) => row.id}
                    rows={drivers}
                    columns={columns}
                    loading={loading}
                    localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </main>
      </div>
    </>
  );
}
